// Global imports
import { useCallback, useEffect, useState } from 'react'

// Types
import type { RefObject } from 'react'

// Tracks whether a horizontally scrolling element has more to see on either
// side, and scrolls it a step at a time, for drawing scroll arrows
const useScrollOverflow = (ref: RefObject<HTMLElement>) => {
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  // A pixel of slack absorbs sub-pixel rounding at either end
  const update = useCallback(() => {
    const el = ref.current

    if (!el) return

    setCanScrollPrev(el.scrollLeft > 1)

    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [ref])

  // Re-checks on scroll, and whenever the element or its children change size
  // (a resize, or the web fonts loading in and widening the items)
  useEffect(() => {
    const el = ref.current

    if (!el) return

    update()

    el.addEventListener('scroll', update, { passive: true })

    const observer = new ResizeObserver(update)

    observer.observe(el)

    Array.from(el.children).forEach(child => observer.observe(child))

    return () => {
      el.removeEventListener('scroll', update)

      observer.disconnect()
    }
  }, [ref, update])

  // Moves most of the visible width, so a partly hidden item comes into view
  const scrollStep = (direction: 1 | -1) => {
    const el = ref.current

    if (!el) return

    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' })
  }

  return { canScrollPrev, canScrollNext, scrollStep }
}

export default useScrollOverflow
